import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button, Navbar, Nav } from 'react-bootstrap';
import { trackEvent, trackPageView } from '../utils/analytics';

// Where interested contractors are sent. No backend yet — the CTA opens an
// email so we can collect early-access interest without a form/database.
const CONTACT_EMAIL = 'pro@plum-app.com';
const CONTACT_SUBJECT = 'Plum Pro early access';
const CONTACT_MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(CONTACT_SUBJECT)}`;

function ProLandingPage() {
  useEffect(() => {
    trackPageView('/pro');
  }, []);

  const handleCta = (source) => {
    trackEvent('pro_cta_click', { source });
  };

  const features = [
    {
      icon: 'bi-telephone-inbound',
      title: 'Never Miss a Lead',
      description:
        'Every inbound call, text, and email gets an instant, professional response — 24/7. Plum Pro engages the moment a homeowner reaches out, so leads never go cold while you’re on the job.'
    },
    {
      icon: 'bi-clipboard-check',
      title: 'Skip the First Visit',
      description:
        'Replace the initial in-person consultation with a guided AI conversation that captures dimensions, materials, finishes, photos, and scope — everything you’d gather on site, without the drive.'
    },
    {
      icon: 'bi-funnel',
      title: 'Qualify Before You Drive',
      description:
        'Plum Pro surfaces budget, timeline, and project fit up front. You spend windshield time only on jobs that are real, ready, and worth quoting.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'A Lead Comes In',
      description:
        'A homeowner calls, texts, or emails about a project — say, replacing an exterior door. Plum Pro responds instantly in your name and starts the conversation.'
    },
    {
      number: '2',
      title: 'Plum Pro Runs the Consult',
      description:
        'Through a natural back-and-forth, it gathers measurements, materials, finishes, photos, budget, and timeline — the same questions you’d ask on a first visit, asked for you.'
    },
    {
      number: '3',
      title: 'You Get a Ready-to-Quote Spec',
      description:
        'A complete, structured job specification lands in your inbox — qualified and ready to price. No wasted truck rolls, no back-and-forth, just jobs worth winning.'
    }
  ];

  return (
    <div className="auth-page">
      {/* Navigation */}
      <Navbar className="landing-nav" sticky="top" expand={false}>
        <Container>
          <Navbar.Brand className="d-flex align-items-center gap-2">
            <img src="/apple-icon-180x180.png" alt="Plum Pro" style={{ width: 32, height: 32, borderRadius: 8 }} />
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
              Plum <span style={{ color: 'var(--orange-accent)' }}>Pro</span>
            </span>
          </Navbar.Brand>
          <Nav className="d-flex align-items-center gap-3">
            <a href="https://www.plum-app.com" className="landing-footer-link">For Homeowners</a>
            <Button
              variant="outline-dark"
              className="landing-nav-btn"
              href={CONTACT_MAILTO}
              onClick={() => handleCta('nav')}
              size="sm"
            >
              Request Access
            </Button>
          </Nav>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section className="landing-hero">
        <Container style={{ maxWidth: 820 }}>
          <div className="text-center">
            <div className="mb-4">
              <img src="/apple-icon-180x180.png" alt="Plum Pro" style={{ width: 80, height: 80, borderRadius: 20 }} />
            </div>
            <h1 className="landing-hero-title">
              Qualify Every Lead<br />
              <span style={{ fontSize: '1.75rem', fontWeight: 600 }}>Without the First Visit</span>
            </h1>
            <p className="landing-hero-subtitle">
              Plum Pro talks to your inbound leads by phone, text, and email, builds a complete contractor-ready job specification, and hands you only the jobs worth a truck roll.
            </p>

            <Button
              variant="outline-dark"
              size="lg"
              className="cta-btn d-inline-flex align-items-center justify-content-center gap-2"
              href={CONTACT_MAILTO}
              onClick={() => handleCta('hero')}
            >
              Request Early Access
            </Button>
            <p className="landing-hero-note mt-3">Built for contractors. No setup fees during early access.</p>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="landing-section">
        <Container>
          <h2 className="landing-section-title text-center">Why Contractors Choose Plum Pro</h2>
          <p className="landing-section-subtitle text-center">
            Turn every inbound inquiry into a qualified, ready-to-quote job
          </p>
          <Row className="g-4 mt-2">
            {features.map((feature, idx) => (
              <Col md={4} key={idx}>
                <Card className="landing-feature-card h-100">
                  <Card.Body className="p-4 text-center">
                    <div className="landing-feature-icon mb-3">
                      <i className={`bi ${feature.icon}`}></i>
                    </div>
                    <h5 className="mb-3" style={{ color: 'var(--text-primary)' }}>{feature.title}</h5>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>{feature.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="landing-section landing-section-alt">
        <Container>
          <h2 className="landing-section-title text-center">How It Works</h2>
          <p className="landing-section-subtitle text-center">
            From first contact to a quote-ready spec — hands off
          </p>
          <Row className="g-4 mt-2 justify-content-center">
            {steps.map((step, idx) => (
              <Col md={4} key={idx} className="text-center">
                <div className="landing-step">
                  <div className="landing-step-number">{step.number}</div>
                  <h5 className="mt-3 mb-2" style={{ color: 'var(--text-primary)' }}>{step.title}</h5>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>{step.description}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="landing-cta">
        <Container className="text-center" style={{ maxWidth: 600 }}>
          <h2 className="landing-section-title">Stop Driving to Dead-End Leads</h2>
          <p className="landing-hero-subtitle">
            Join the contractors using Plum Pro to qualify inbound leads automatically and reclaim their time for the jobs that pay.
          </p>
          <Button
            size="lg"
            className="cta-btn d-inline-flex align-items-center justify-content-center gap-2"
            href={CONTACT_MAILTO}
            onClick={() => handleCta('final-cta')}
          >
            Request Early Access
          </Button>
        </Container>
      </section>

      {/* Footer */}
      <footer className="landing-footer text-center">
        <Container>
          <p className="mb-2">&copy; 2026 Plum Pro. AI-powered lead qualification for contractors.</p>
          <p className="mb-0">
            <a href="https://www.plum-app.com" className="landing-footer-link">For Homeowners</a>
            <span className="mx-2">|</span>
            <a href="/terms" className="landing-footer-link">Terms of Service</a>
            <span className="mx-2">|</span>
            <a href="/privacy" className="landing-footer-link">Privacy Policy</a>
          </p>
        </Container>
      </footer>
    </div>
  );
}

export default ProLandingPage;
